import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../../components/UI/Button';
import { useContracts, selectFundCoverage } from './ContractsContext';
import { ContractOverflowMenu } from './ContractDetailPage';
import type { ContractBundle } from './types';

type ViewMode = 'client' | 'creator';
type FilterTab = 'all' | 'pending_approval' | 'active' | 'completed' | 'cancelled' | 'draft';

const formatRmb = (n: number) => `¥${Math.round(n).toLocaleString('zh-CN')}`;
const formatDate = (ms?: number) => ms ? new Date(ms).toLocaleDateString('zh-CN') : '—';

// V2 acceptanceState → the coarse status chip shown on the card.
const stateChip = (state: string): { label: string; cls: string } => {
  switch (state) {
    case 'draft':               return { label: '草稿',       cls: 'bg-slate-100 text-slate-600 border-slate-200' };
    case 'pending_other_side':  return { label: '待审核',     cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    case 'rejected':            return { label: '已拒绝',     cls: 'bg-rose-100 text-rose-700 border-rose-200' };
    case 'active':              return { label: '执行中',     cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    case 'completed':           return { label: '已完成',     cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    case 'cancelled':           return { label: '已取消',     cls: 'bg-slate-100 text-slate-500 border-slate-200' };
    case 'disputed':            return { label: '争议中',     cls: 'bg-rose-100 text-rose-700 border-rose-200' };
    default:                    return { label: '草稿',       cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  }
};

const ContractCard: React.FC<{ bundle: ContractBundle; viewMode: ViewMode }> = ({ bundle, viewMode }) => {
  const navigate = useNavigate();
  const { contract, milestones } = bundle;
  const cov = selectFundCoverage(bundle);
  const chip = stateChip(contract.acceptanceState);
  const completedCount = milestones.filter(m => m.status === 'COMPLETED').length;
  const otherParty = viewMode === 'client' ? contract.creator : contract.client;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={() => navigate(`/contracts/${contract.id}`)}
      className="group bg-white rounded-2xl border border-slate-100 transition-all duration-300 hover:shadow-xl hover:border-blue-100 hover:-translate-y-1 cursor-pointer flex flex-col relative"
    >
      {/* Header */}
      <div className="p-6 border-b border-slate-50 relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${chip.cls}`}>
                {chip.label}
              </span>
              <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                contract.projectType === 'large' ? 'bg-purple-50 text-purple-700' : 'bg-sky-50 text-sky-700'
              }`}>
                {contract.projectType === 'large' ? '大项目' : '小项目'}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2">
              {contract.title}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              对方：
              <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">
                {otherParty.nickname || otherParty.userId}
              </span>
            </p>
          </div>

          {/* 3-dots menu — same actions as the detail page (取消合同 /
              发起申诉) via the shared ContractOverflowMenu. Wrapped so
              clicks (including its confirm modals) don't bubble to the
              card's navigate-to-detail handler. */}
          <div className="shrink-0" onClick={stop}>
            <ContractOverflowMenu bundle={bundle} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-2xl font-bold text-slate-900">{formatRmb(contract.totalAmount)}</div>
            <div className="text-xs text-slate-400 mt-0.5">合同总额</div>
          </div>
          {contract.projectType === 'large' && (
            <div className="text-right">
              <div className="text-sm font-semibold text-slate-700">{completedCount}/{milestones.length}</div>
              <div className="text-xs text-slate-400 mt-0.5">里程碑</div>
            </div>
          )}
        </div>

        {contract.projectType === 'large' && contract.acceptanceState === 'active' && (
          <div className="grid grid-cols-3 gap-3 mb-4 pt-3 border-t border-slate-50 text-xs">
            <div>
              <div className="font-semibold text-slate-900">{formatRmb(cov.totalPaid)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">已付款</div>
            </div>
            <div>
              <div className="font-semibold text-slate-900">{formatRmb(cov.totalReleased)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">已放款</div>
            </div>
            <div>
              <div className={`font-semibold ${cov.availableBalance > 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                {formatRmb(cov.availableBalance)}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">资金余额</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Calendar className="w-3.5 h-3.5" />
            创建于 {formatDate(contract.createdAt)}
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
            查看详情 <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  );
};

const VIEW_MODES: ViewMode[] = ['client', 'creator'];
const FILTER_TABS: FilterTab[] = ['all', 'pending_approval', 'active', 'completed', 'cancelled', 'draft'];

const ContractsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { bundles, viewerId, loading } = useContracts();
  // Persist role + tab in the URL so returning from a contract detail
  // (or refreshing / sharing the link) restores the same view instead of
  // snapping back to 我是需求方.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: ViewMode = VIEW_MODES.includes(searchParams.get('role') as ViewMode)
    ? (searchParams.get('role') as ViewMode) : 'client';
  const activeTab: FilterTab = FILTER_TABS.includes(searchParams.get('tab') as FilterTab)
    ? (searchParams.get('tab') as FilterTab) : 'all';

  const setViewMode = (mode: ViewMode) =>
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('role', mode); return n; }, { replace: true });
  const setActiveTab = (tab: FilterTab) =>
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', tab); return n; }, { replace: true });

  // Bundles where the viewer plays the selected role AND the contract has
  // actually started (a project only exists once signing is done). Pending
  // / draft / rejected / cancelled contracts are records, not projects —
  // they live in 我的合同, so 项目管理 (incl. its 全部 tab) never shows them.
  const PROJECT_STATES = ['active', 'completed', 'disputed'];
  const roleBundles = useMemo(() => bundles.filter(b =>
    (viewMode === 'client'
      ? b.contract.client.userId === viewerId
      : b.contract.creator.userId === viewerId)
    && PROJECT_STATES.includes(b.contract.acceptanceState)
  ), [bundles, viewMode, viewerId]);

  const matchesTab = (state: string, tab: FilterTab): boolean => {
    switch (tab) {
      case 'all':              return true;
      case 'pending_approval': return state === 'pending_other_side';
      case 'active':           return state === 'active';
      case 'completed':        return state === 'completed';
      case 'cancelled':        return state === 'cancelled' || state === 'rejected';
      case 'draft':            return state === 'draft';
      default:                 return true;
    }
  };

  const filtered = useMemo(
    () => roleBundles.filter(b => matchesTab(b.contract.acceptanceState, activeTab)),
    [roleBundles, activeTab],
  );

  const counts = useMemo(() => ({
    all: roleBundles.length,
    pending_approval: roleBundles.filter(b => matchesTab(b.contract.acceptanceState, 'pending_approval')).length,
    active: roleBundles.filter(b => matchesTab(b.contract.acceptanceState, 'active')).length,
    completed: roleBundles.filter(b => matchesTab(b.contract.acceptanceState, 'completed')).length,
    cancelled: roleBundles.filter(b => matchesTab(b.contract.acceptanceState, 'cancelled')).length,
    draft: roleBundles.filter(b => matchesTab(b.contract.acceptanceState, 'draft')).length,
  }), [roleBundles]);

  // 项目管理 only surfaces the delivery-phase statuses; contract-record
  // states (待审核/已取消) live in 我的合同.
  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'active', label: '执行中' },
    { key: 'completed', label: '已完成' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-4 group w-fit"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm font-medium">返回</span>
            </button>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <FileText className="w-10 h-10 text-blue-600" />
              项目管理
            </h1>
            <p className="text-sm text-gray-500 mt-2">按里程碑推进交付、验收与放款。发起新合同或查看已签署的合同记录请前往<button onClick={() => navigate('/my-contracts')} className="text-blue-600 hover:underline mx-0.5">我的合同</button>。</p>
          </div>
        </div>

        {/* View mode + status tabs */}
        <div className="mb-8 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="inline-flex bg-slate-100 rounded-xl p-1" data-tour="contracts-role-toggle">
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'client' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('client')}
            >
              我是需求方
            </button>
            <button
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'creator' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setViewMode('creator')}
            >
              我是创作者
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 bg-gray-50/50 p-1.5 rounded-[2rem] w-fit xl:ml-auto border border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                {counts[tab.key] > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {counts[tab.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm">加载中...</div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filtered.map((b) => (
              <ContractCard
                key={b.contract.id}
                bundle={b}
                viewMode={viewMode}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
              <FileText className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-xl font-medium text-slate-900 mb-2">暂无进行中的项目</h3>
            <p className="text-slate-600 mb-6">
              {activeTab === 'all'
                ? viewMode === 'client' ? '你还没有作为需求方的进行中项目' : '你还没有作为创作者的进行中项目'
                : `暂无${tabs.find(t => t.key === activeTab)?.label}的项目`}
            </p>
            <Button variant="primary" onClick={() => navigate('/my-contracts')}>
              <Plus className="w-5 h-5 mr-2" />
              发起新合同
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractsListPage;
